import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import * as L from "lucide-react";
import {
  ChevronRight, Search, ChevronDown, FileText, Clock,
  ShieldCheck, Database, Lock, BadgeCheck, Gavel, Wallet,
  ShieldAlert, AlertCircle, Calendar,
} from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { useContent } from "@/lib/cms";

// Map of tone → tailwind classes for callout cards
const CALLOUT_TONES = {
  emerald: { ring: "ring-emerald-100", bg: "bg-emerald-50/80", iconBg: "bg-emerald-100 text-emerald-700", title: "text-emerald-900", body: "text-emerald-900/80" },
  blue:    { ring: "ring-blue-100",    bg: "bg-blue-50/80",    iconBg: "bg-blue-100 text-blue-700",       title: "text-blue-900",    body: "text-blue-900/80" },
  amber:   { ring: "ring-amber-100",   bg: "bg-amber-50/80",   iconBg: "bg-amber-100 text-amber-700",     title: "text-amber-900",   body: "text-amber-900/80" },
  rose:    { ring: "ring-rose-100",    bg: "bg-rose-50/80",    iconBg: "bg-rose-100 text-rose-700",       title: "text-rose-900",    body: "text-rose-900/80" },
  violet:  { ring: "ring-violet-100",  bg: "bg-violet-50/80",  iconBg: "bg-violet-100 text-violet-700",   title: "text-violet-900",  body: "text-violet-900/80" },
  sky:     { ring: "ring-sky-100",     bg: "bg-sky-50/80",     iconBg: "bg-sky-100 text-sky-700",         title: "text-sky-900",     body: "text-sky-900/80" },
  slate:   { ring: "ring-slate-200",   bg: "bg-slate-50",      iconBg: "bg-slate-200 text-slate-700",     title: "text-slate-900",   body: "text-slate-700" },
};
const FALLBACK_ICONS = { ShieldCheck, Database, Lock, BadgeCheck, Gavel, Wallet, ShieldAlert, BadgeIndianRupee: BadgeCheck };

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/&amp;/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

/* (Search highlights are now baked into the parsed HTML inside the useMemo
   `processedHtml` pipeline, so they survive any React re-render.) */


const Callout = ({ icon, title, body, tone }) => {
  const t = CALLOUT_TONES[tone] || CALLOUT_TONES.slate;
  const Icon = L[icon] || FALLBACK_ICONS[icon] || AlertCircle;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4 }}
      data-testid={`legal-callout-${slugify(title)}`}
      className={`rounded-2xl border bg-white ring-1 ${t.ring} ${t.bg} p-4 sm:p-5 flex gap-4`}
    >
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${t.iconBg}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className={`font-bold text-sm tracking-tight ${t.title}`}>{title}</div>
        <p className={`mt-1 text-[13.5px] leading-relaxed ${t.body}`}>{body}</p>
      </div>
    </motion.div>
  );
};

const FALLBACK_PAGE = {
  eyebrow: "Legal",
  title: "Document",
  description: "",
  last_updated: "",
  body_html: "<p>This page has not been published yet.</p>",
  callouts: [],
  seo: {},
};

export default function LegalPage({ slug, fallbackTitle = "Legal" }) {
  const { data } = useContent("legal_pages", { pages: [] });
  const page = useMemo(() => {
    const pages = (data && data.pages) || [];
    return pages.find((p) => p.slug === slug) || { ...FALLBACK_PAGE, title: fallbackTitle };
  }, [data, slug, fallbackTitle]);

  // SEO — patch <title> and meta tags dynamically
  useEffect(() => {
    const seo = page.seo || {};
    const prevTitle = document.title;
    document.title = seo.title || `${page.title} · Wappit`;

    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setOG = (prop, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel, href) => {
      if (!href) return;
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    setMeta("description", seo.description);
    setMeta("robots", seo.robots || "index,follow");
    setOG("og:title", seo.og_title || seo.title);
    setOG("og:description", seo.og_description || seo.description);
    setOG("og:image", seo.og_image);
    setLink("canonical", seo.canonical);
    return () => { document.title = prevTitle; };
  }, [page]);

  // ----- Pre-process body HTML: inject ids into each <h2> so they survive React's
  //       dangerouslySetInnerHTML cycles. Also derive the TOC from the same pass. -----
  const bodyRef = useRef(null);
  const spyLockRef = useRef({ id: null, until: 0 });
  const [activeId, setActiveId] = useState(null);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [matchCount, setMatchCount] = useState(0);

  const { processedHtml, toc, searchMatches } = useMemo(() => {
    const html = page.body_html || "";
    if (typeof window === "undefined" || !window.DOMParser) {
      return { processedHtml: html, toc: [], searchMatches: 0 };
    }
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
    const root = doc.body.firstElementChild;
    if (!root) return { processedHtml: html, toc: [], searchMatches: 0 };
    const seen = {};
    const list = [];
    root.querySelectorAll("h2").forEach((h) => {
      let base = slugify(h.textContent || "section");
      if (!base) base = "section";
      let id = base;
      let i = 1;
      while (seen[id]) { id = `${base}-${i++}`; }
      seen[id] = true;
      h.id = id;
      h.setAttribute("data-legal-h2", "true");
      list.push({ id, text: h.textContent || "Section" });
    });

    // Apply search highlights inside the HTML so they survive any React re-render
    let matches = 0;
    const q = (query || "").trim();
    if (q.length >= 2) {
      const re = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
      const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
          if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
          const tag = node.parentElement?.tagName;
          if (tag === "SCRIPT" || tag === "STYLE" || tag === "MARK") return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      const targets = [];
      let cur = walker.nextNode();
      while (cur) { targets.push(cur); cur = walker.nextNode(); }
      targets.forEach((node) => {
        if (!re.test(node.nodeValue)) return;
        re.lastIndex = 0;
        const frag = doc.createDocumentFragment();
        let lastIdx = 0;
        let m;
        while ((m = re.exec(node.nodeValue)) !== null) {
          const before = node.nodeValue.slice(lastIdx, m.index);
          if (before) frag.appendChild(doc.createTextNode(before));
          const mark = doc.createElement("mark");
          mark.className = "legal-hl";
          mark.textContent = m[0];
          frag.appendChild(mark);
          lastIdx = m.index + m[0].length;
          matches += 1;
        }
        const tail = node.nodeValue.slice(lastIdx);
        if (tail) frag.appendChild(doc.createTextNode(tail));
        node.parentNode.replaceChild(frag, node);
      });
    }

    return { processedHtml: root.innerHTML, toc: list, searchMatches: matches };
  }, [page.body_html, query]);

  // Keep matchCount in sync with the memoised parse
  useEffect(() => { setMatchCount(searchMatches); }, [searchMatches]);

  // Scroll to the first match whenever the query changes (only on user input, not on render)
  useEffect(() => {
    if (!query || query.trim().length < 2 || !bodyRef.current) return;
    // Defer until React has committed the new innerHTML
    const id = requestAnimationFrame(() => {
      const first = bodyRef.current?.querySelector("mark.legal-hl");
      if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => cancelAnimationFrame(id);
  }, [query, processedHtml]);

  // Initialise active section to the first heading on first load
  useEffect(() => {
    if (toc.length > 0) {
      setActiveId((prev) => prev || toc[0].id);
    }
  }, [toc]);

  // Scroll-spy: rAF-debounced scroll listener. We re-query heading elements
  // on every tick (refs go stale when React re-applies dangerouslySetInnerHTML).
  useEffect(() => {
    if (toc.length === 0) return;
    const TRIGGER_OFFSET = 120;
    let raf = null;

    const compute = () => {
      raf = null;
      const now = performance.now();
      const lock = spyLockRef.current;
      if (lock.id && now < lock.until) {
        setActiveId((prev) => (prev === lock.id ? prev : lock.id));
        return;
      }
      // Walk the TOC ids and find the heading whose top has crossed the trigger line
      let activeIdNext = toc[0].id;
      for (let i = 0; i < toc.length; i++) {
        const el = document.getElementById(toc[i].id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top - TRIGGER_OFFSET <= 0.5) activeIdNext = toc[i].id;
        else break;
      }
      setActiveId((prev) => (prev === activeIdNext ? prev : activeIdNext));
    };

    const onScroll = () => {
      if (raf == null) raf = requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [toc]);

  // Honour `#section` anchor on mount, on `hashchange`, and on Back/Forward
  useEffect(() => {
    if (toc.length === 0) return;
    const tryJumpToHash = () => {
      const hash = (window.location.hash || "").replace(/^#/, "");
      if (!hash) return;
      const exists = toc.some((t) => t.id === hash);
      if (!exists) return;
      requestAnimationFrame(() => {
        const el = document.getElementById(hash);
        if (!el) return;
        const TRIGGER_OFFSET = 100;
        const top = el.getBoundingClientRect().top + window.scrollY - TRIGGER_OFFSET;
        spyLockRef.current = { id: hash, until: performance.now() + 900 };
        setActiveId(hash);
        window.scrollTo({ top, behavior: "smooth" });
      });
    };
    tryJumpToHash();
    window.addEventListener("hashchange", tryJumpToHash);
    window.addEventListener("popstate", tryJumpToHash);
    return () => {
      window.removeEventListener("hashchange", tryJumpToHash);
      window.removeEventListener("popstate", tryJumpToHash);
    };
  }, [toc]);

  const jumpTo = (id) => {
    const wasMobileOpen = mobileTocOpen;
    setMobileTocOpen(false);

    const performScroll = () => {
      const el = document.getElementById(id);
      if (!el) return;
      const TRIGGER_OFFSET = 100;
      const top = el.getBoundingClientRect().top + window.scrollY - TRIGGER_OFFSET;
      // Lock active state during smooth-scroll animation so it doesn't flicker through intermediate sections
      spyLockRef.current = { id, until: performance.now() + 900 };
      setActiveId(id);
      window.scrollTo({ top, behavior: "smooth" });
      // Reflect the jump in the URL without re-triggering scroll
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, "", `#${id}`);
      }
    };

    // If the mobile accordion is collapsing, wait for the animation to finish
    // (otherwise the layout shrinks mid-scroll and we land at the wrong y-coordinate).
    if (wasMobileOpen) {
      setTimeout(performScroll, 280);
    } else {
      performScroll();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* ────────── Hero ────────── */}
      <section className="relative pt-28 sm:pt-32 pb-12 sm:pb-16 bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 border-b border-slate-100 overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage: "radial-gradient(circle at 25% 30%, #d1fae5 0%, transparent 35%), radial-gradient(circle at 80% 60%, #dbeafe 0%, transparent 35%)",
          }} aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="text-[13px] text-slate-500 flex items-center gap-1.5" data-testid="legal-breadcrumb">
            <Link to="/" className="hover:text-emerald-700 transition-colors font-medium">Home</Link>
            <ChevronRight className="h-3 w-3 text-slate-400" />
            <span className="text-slate-900 font-semibold">{page.title}</span>
          </nav>

          <div className="mt-6 flex items-end justify-between gap-6 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-[11px] font-bold tracking-[0.16em] uppercase">
                <FileText className="h-3 w-3" />
                {page.eyebrow || "Legal"}
              </div>
              <h1
                className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight"
                style={{ fontFamily: "Outfit, sans-serif" }}
                data-testid="legal-title"
              >
                {page.title}
              </h1>
              {page.description && (
                <p className="mt-4 text-lg text-slate-600 max-w-2xl">{page.description}</p>
              )}
              {page.last_updated && (
                <div className="mt-5 inline-flex items-center gap-2 text-[13px] text-slate-500" data-testid="legal-last-updated">
                  <Clock className="h-3.5 w-3.5" />
                  <span>Last updated</span>
                  <span className="font-bold text-slate-700">{page.last_updated}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ────────── Callouts ────────── */}
      {Array.isArray(page.callouts) && page.callouts.length > 0 && (
        <section className="bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 sm:py-12">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {page.callouts.map((c, idx) => <Callout key={`${c.title}-${idx}`} {...c} />)}
            </div>
          </div>
        </section>
      )}

      {/* ────────── Body + sticky TOC ────────── */}
      <section className="bg-slate-50/40 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 sm:py-14 grid lg:grid-cols-[260px_1fr] gap-8 lg:gap-12">
          {/* TOC — desktop sticky */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-3">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(2,6,23,0.04),0_8px_24px_rgba(2,6,23,0.04)] overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100">
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">On this page</div>
                </div>
                <nav className="px-2 py-2 max-h-[60vh] overflow-y-auto" data-testid="legal-toc-desktop">
                  {toc.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No sections yet.</div>}
                  {toc.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => jumpTo(t.id)}
                      data-testid={`toc-item-${t.id}`}
                      className={`block w-full text-left px-3 py-1.5 rounded-md text-[13px] leading-snug transition-all ${
                        activeId === t.id
                          ? "bg-emerald-50 text-emerald-700 font-bold border-l-2 border-emerald-500 pl-2.5"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                      }`}
                    >
                      {t.text}
                    </button>
                  ))}
                </nav>
              </div>
              {page.last_updated && (
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-2 text-[12px] text-slate-500">
                  <Calendar className="h-3.5 w-3.5 text-slate-400" />
                  Last updated <span className="ml-auto font-bold text-slate-700">{page.last_updated}</span>
                </div>
              )}
            </div>
          </aside>

          {/* Body */}
          <div>
            {/* Search bar */}
            <div className="mb-6 rounded-2xl border border-slate-200 bg-white px-3 sm:px-4 py-2.5 flex items-center gap-3" data-testid="legal-search-bar">
              <Search className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search this page — try “refund”, “wallet”, “meta”, “jurisdiction”…"
                className="flex-1 bg-transparent text-[14px] focus:outline-none placeholder:text-slate-400"
                data-testid="legal-search-input"
              />
              {query && (
                <span className="text-[11px] font-bold text-slate-500">
                  {matchCount} match{matchCount === 1 ? "" : "es"}
                </span>
              )}
            </div>

            {/* Mobile TOC accordion */}
            <div className="lg:hidden mb-6 rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="legal-toc-mobile">
              <button
                type="button"
                onClick={() => setMobileTocOpen((v) => !v)}
                data-testid="legal-toc-mobile-toggle"
                className="w-full flex items-center justify-between px-4 py-3 text-left"
              >
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">On this page</span>
                <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${mobileTocOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {mobileTocOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.22, ease: "easeOut" }}
                    className="overflow-hidden border-t border-slate-100"
                  >
                    <div className="px-2 py-2">
                      {toc.map((t) => (
                        <button key={t.id} type="button" onClick={() => jumpTo(t.id)}
                          className={`block w-full text-left px-3 py-1.5 rounded-md text-[13px] ${
                            activeId === t.id ? "bg-emerald-50 text-emerald-700 font-bold" : "text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          {t.text}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Rendered HTML — explicit legal-prose typography (ids injected into H2s) */}
            <article
              ref={bodyRef}
              className="legal-prose max-w-[72ch]"
              dangerouslySetInnerHTML={{ __html: processedHtml }}
              data-testid="legal-body"
            />
          </div>
        </div>
      </section>

      <Footer activeLegal={slug} />
    </div>
  );
}
